package com.siriusxm.pia.views.unifiedaggregator.types

import androidx.compose.runtime.*
import com.siriusxm.cmc.charts.Chart
import com.siriusxm.cmc.charts.ChartDetails
import com.siriusxm.cmc.charts.ChartType
import com.siriusxm.cmc.charts.SingleColor
import com.siriusxm.cmc.smithy.EntityModelStyles
import com.siriusxm.cmc.smithy.Shape
import com.siriusxm.cmc.smithy.exampleFromPattern
import com.siriusxm.cmc.smithy.exampleJson
import com.siriusxm.cmc.utils.Route
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.cmc.utils.toAMPMTimeString
import com.siriusxm.cmc.utils.toLocalDateTime
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.EntityTypeDetailDestination
import com.siriusxm.pia.views.unifiedaggregator.EntityTypeTab
import com.siriusxm.pia.views.unifiedaggregator.ProducerDetail
import com.siriusxm.pia.views.unifiedaggregator.NavLink
import com.siriusxm.pia.views.unifiedaggregator.Query
import com.siriusxm.pia.views.unifiedaggregator.navigate
import com.siriusxm.pia.views.unifiedaggregator.backfill.toLocaleString
import com.siriusxm.pia.views.unifiedaggregator.dataflow.dataFlowVisualization
import com.siriusxm.pia.views.unifiedaggregator.events.EntityComparisonDialog
import com.siriusxm.pia.views.unifiedaggregator.events.EntityComparisonResult
import com.siriusxm.pia.views.unifiedaggregator.events.EventFilters
import com.siriusxm.pia.views.unifiedaggregator.events.compareJsonWithReference
import unifiedcontent.aggregator.*
import kotlinx.datetime.LocalDate
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.hours
import kotlin.time.Instant

private val PrettyPrintJson = Json { prettyPrint = true }
internal val SCROLL_SMOOTH_START = js("({behavior: 'smooth', block: 'start'})")
internal val SCROLL_SMOOTH_NEAREST = js("({behavior: 'smooth', block: 'nearest'})")

/**
 * Renders the main data types view, which includes entity types and partials.
 */
@Composable
fun ServiceViewContext.dataTypes(
    context: AggregatorService,
    route: Route
) {
    var selection = "dataflow"
    var initialSelect: String? = null
    var initialTab: EntityTypeTab? = null
    route.switch {
        select("dataflow") {
            // Data flow tab - no sub-selection
        }
        select("entities") {
            selection = "entities"
            switch {
                select(Regex(".+")) {
                    initialSelect = match.takeIf { it.isNotBlank() }
                    switch {
                        select(Regex(".+")) {
                            initialTab = EntityTypeTab.fromId(match.takeIf { it.isNotBlank() })
                        }
                        default {
                        }
                    }
                }
                default {
                }
            }
        }
        select("partials") {
            selection = "partials"
            switch {
                select(Regex(".*")) {
                    initialSelect = match.takeIf { it.isNotBlank() }
                }
                default {
                }
            }
        }
    }

    serviceContentView({
        fixedHeight = true
        breadcrumbs {
            crumb("Aggregator", "aggregator") {
                crumb("Types", "types".takeIf { initialSelect != null }) {
                    val typeName = initialSelect?.let { context.entityType(it)?.name }
                    if (typeName != null) {
                        crumb(typeName, null)
                    }
                }
            }
        }
    }) {
        tabView {
            fixedHeight = true
            this.onSelect {
                context.navigate("aggregator/types/${it}")
                false
            }
            this.selection = selection
            tab("Data Flow", "dataflow") {
                dataFlowVisualization(context)
            }
            tab("Entity Types", "entities") {
                entityTypes(
                    context,
                    initialSelect = initialSelect,
                    initialTab = initialTab,
                    filters = EventFilters.from(Query.parse(route.fullPath.substringAfter("?", "")))
                )
            }
            if (context.partials.isNotEmpty()) {
                tab("Partial Schemas", "partials") {
                    partials(context, initialSelect = initialSelect)
                }
            }
        }
    }
}

/**
 * Renders the entity types
 */
@Composable
fun entityTypes(
    context: AggregatorService,
    initialSelect: String? = null,
    initialTab: EntityTypeTab? = null,
    filters: EventFilters = EventFilters()
) {
    Style(EntityTypesStyles)

    val consumers = context.consumers
    val producers = context.producers

    splitSelectionView<EntityTypeConfiguration> {
        fixedHeight = true
        items = context.entityTypes
        selection = initialSelect?.let {
            context.entityTypes.find { it.type == initialSelect }
        }

        renderListItem {
            it.name
        }

        render {
            entityType(context, it, producers, consumers, initialTab, filters)
        }

        onSelect {
            context.navigate(EntityTypeDetailDestination(it.type))
        }

        column {
            title = "Version"
            content {
                Text(it.version)
            }
        }

        column {
            title = "Typed Entity Prefix"
            content {
                val prefix = it.typedEntityPrefix
                if (!prefix.isNullOrBlank()) {
                    Text(prefix)
                }
            }
        }

        column {
            title = "Count"
            // Fixed width so the column does not resize as counts of wildly different magnitudes
            // page in and out; 120px comfortably holds the widest realistic value (~14,321,326).
            width = 120.px
            // Right-aligned so magnitudes line up down the column; counts span 1 to ~14M.
            style { textAlign("right") }
            content {
                // A null count means the snapshot did not count this type. Render nothing —
                // never a zero, which would claim the type has no entities.
                it.approximateEntityCount?.let { count -> Text(count.formattedCount()) }
            }
        }
    }
}

/**
 * Format an approximate entity count with locale thousands separators.
 *
 * Converts to [Double] first, deliberately. Kotlin/JS compiles [Long] to a class object rather
 * than a JS number, so calling `toLocaleString()` on it falls through to `Object.prototype`
 * and yields unseparated digits; a [Double] is a real JS number and picks up
 * `Number.prototype`'s digit grouping. Counts top out around 14.3 million, far inside the
 * range a Double represents exactly.
 */
private fun Long.formattedCount(): String = toDouble().toLocaleString()

data class DataPoint(val ts: Instant, val value: Double)

fun MetricData.allData(period: TimeRange, loadTime: Instant?, groupPeriod: Duration): List<DataPoint> {
    val range = period.range(loadTime ?: Clock.System.now())
    var instant = range.endInclusive
    val results = arrayListOf<DataPoint>()
    do {
        val index = this.timestamps?.indexOfFirst {
            it <= instant && it >= instant - groupPeriod
        }?.takeIf { it >= 0 }
        val value = index?.let { this.values?.get(index) }

        // we don't include the most recent data point if it's null
        if (value != null || instant != range.endInclusive) {
            results += DataPoint(instant, value ?: 0.0)
        }
        instant -= groupPeriod
    } while (instant > range.start)
    return results.toList().reversed()
}

/**
 * Deep blue used for the primary series on this view's charts.
 *
 * A literal rather than an [SXMUI] colour because Chart.js paints to a canvas and needs a
 * resolved colour string, whereas the SXMUI palette is exposed as theme-dependent CSS custom
 * properties with no resolver to a literal. Shared between the published-events chart and the
 * count-history chart so there is one blue rather than two that drift apart.
 */
const val CHART_SERIES_BLUE = "rgb(34, 68, 136)"

val colors = mapOf(
    "updateEvents" to arrayOf(CHART_SERIES_BLUE),
    "addEvents" to arrayOf("rgb(102, 221, 230)"),
    "removeEvents" to arrayOf("rgb(255, 153, 128)")
)

/**
 * Styles for the entity types view, including layout for metrics displays and related UI elements.
 */
object EntityTypesStyles : StyleSheet() {
    val metricsHeader by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        width(100.percent)
        padding(5.px, 0.px)
    }

    val technicalValue by style {
        fontFamily("monospace")
        property("letter-spacing", "normal")
        property("overflow-wrap", "anywhere")
    }
}

/**
 * Display information about an entity type.
 *
 * @param tab which typed Definition/Roles/Events/Metrics destination to show. Definition is only
 *   selectable when [AggregatorService.entityModel] is loaded; otherwise the first registered tab
 *   is selected.
 * @param filters the Events tab's filter selections, decoded from the URL query (UCA-1039). This
 *   page is the only place they are written: it navigates to itself with a new query, which is why
 *   [EntityTypeEvents] below is fully controlled and holds no filter state of its own.
 */
@Composable
fun entityType(
    aggregator: AggregatorService,
    entityType: EntityTypeConfiguration,
    producers: List<ProducerDetails>,
    consumers: List<ConsumerDetails>,
    tab: EntityTypeTab? = null,
    filters: EventFilters = EventFilters()
) {
    val selectedTab = tab?.takeUnless {
        it == EntityTypeTab.Definition && aggregator.entityModel == null
    }

    entityView({
        title = entityType.name
        subTitle = entityType.type + ":" + entityType.version
    }) {
        tabView {
            selection = selectedTab?.id
            onSelect { selectedId ->
                EntityTypeTab.fromId(selectedId)?.let { selected ->
                    aggregator.navigate(EntityTypeDetailDestination(entityType.type, selected))
                }
                false
            }
            if (aggregator.entityModel != null) {
                tab("Definition", EntityTypeTab.Definition.id) {
                    val expandController = remember(entityType) {
                        if (entityType.shapeId != null) ExpandingTableController() else null
                    }
                    var isExpanded by remember(entityType) { mutableStateOf(false) }

                    val definitionKey = "${entityType.type}:${entityType.version}"
                    val exampleJsonText = remember(definitionKey, aggregator.entityModel) {
                        val shapeId = entityType.shapeId ?: return@remember null
                        val model = aggregator.entityModel ?: return@remember null
                        val shape = model.getShape(shapeId) ?: return@remember null
                        val exampleElement = shape.exampleJson()
                            .overrideTypeField(entityType.type)
                            .overrideVersionField(entityType.version)
                        val patchedElement = patchExampleJson(entityType.type, exampleElement, entityType.idPattern)
                        PrettyPrintJson.encodeToString(patchedElement)
                    }
                    var exampleExpanded by remember(definitionKey) { mutableStateOf(false) }

                    val idPattern = entityType.idPattern?.takeIf { it.isNotBlank() }
                    val typedEntityPrefix = entityType.typedEntityPrefix?.takeIf { it.isNotBlank() }
                    // Null when the nightly snapshot did not count this type. Omit the cell
                    // entirely in that case rather than showing a zero.
                    val approximateCount = entityType.approximateEntityCount
                    val hasDetails = idPattern != null || typedEntityPrefix != null || approximateCount != null
                    Column(gap = 20.px) {
                    box({
                        paddedContent = false
                        if (expandController != null || hasDetails) {
                            header({
                                if (hasDetails) {
                                    content {
                                        detailGrid {
                                            typedEntityPrefix?.let { prefix ->
                                                detail("Typed Entity Prefix") {
                                                    Div({ classes(EntityTypesStyles.technicalValue) }) {
                                                        Text(prefix)
                                                    }
                                                }
                                            }
                                            idPattern?.let { pattern ->
                                                detail("Incoming ID Pattern") {
                                                    Div({ classes(EntityTypesStyles.technicalValue) }) {
                                                        Text(pattern)
                                                    }
                                                }
                                            }
                                            approximateCount?.let { count ->
                                                detail("Approximate Entity Count") {
                                                    Div({ classes(EntityTypesStyles.technicalValue) }) {
                                                        Text(count.formattedCount())
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                actionContent {
                                    if (expandController != null) {
                                        button {
                                            title = if (isExpanded) "Collapse All" else "Expand All"
                                            action {
                                                if (isExpanded) {
                                                    expandController.collapseAll()
                                                } else {
                                                    expandController.expandAll()
                                                }
                                                isExpanded = !isExpanded
                                            }
                                        }
                                    }
                                }
                            })
                        }
                    }) {
                        EntityModel(aggregator, entityType, expandController)
                    }

                    key(definitionKey) {
                        var panelElement by remember { mutableStateOf<org.w3c.dom.Element?>(null) }
                        LaunchedEffect(exampleExpanded) {
                            if (exampleExpanded) {
                                panelElement?.scrollIntoView(SCROLL_SMOOTH_START)
                            }
                        }
                        Div({
                            ref { element ->
                                panelElement = element
                                onDispose { panelElement = null }
                            }
                        }) {
                            ExampleJsonPanel(
                                aggregator = aggregator,
                                entityType = entityType,
                                exampleJson = exampleJsonText.orEmpty(),
                                expanded = exampleExpanded,
                                onExpandedChange = { exampleExpanded = it }
                            )
                        }
                    }
                    }
                }
            }

            tab("Roles", EntityTypeTab.Roles.id) {
                Column(gap = 20.px) {
                    box("Producers", {
                        paddedContent = false
                    }) {
                        val matchedProducers =
                            producers.filter { it.allowedTypes.orEmpty().contains(entityType.type) }
                        if (matchedProducers.isEmpty()) {
                            boxMessage("No producers found for this type")
                        } else {
                            table<ProducerDetails> {
                                items(matchedProducers)
                                column {
                                    content {
                                        NavLink(ProducerDetail(it.id)) {
                                            Text(it.name.orEmpty())
                                        }
                                    }
                                }
                            }
                        }
                    }

                    box("Consumers", {
                        paddedContent = false
                    }) {
                        val matchedConsumers = consumers.filter { it.types.orEmpty().contains(entityType.type) }
                        if (matchedConsumers.isEmpty()) {
                            boxMessage("No consumers found for this type.")
                        } else {
                            table<ConsumerDetails> {
                                items(matchedConsumers)
                                column {
                                    content {
                                        A("#aggregator/consumers/${encodeURIComponent(it.id)}") {
                                            Text(it.name ?: "")
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            tab("Events", EntityTypeTab.Events.id) {
                EntityTypeEvents(
                    aggregator,
                    entityType,
                    filters = filters,
                    // Writing a filter is a navigation: same destination, new query. Push rather
                    // than replace, matching Producer's Events tab (UCA-1028), so browser Back
                    // steps back through filter states.
                    onFiltersChange = { updated ->
                        aggregator.navigate(
                            EntityTypeDetailDestination(entityType.type, EntityTypeTab.Events),
                            updated.toQuery()
                        )
                    }
                )
            }

            tab("Metrics", EntityTypeTab.Metrics.id) {
                EntityTypeMetrics(aggregator, entityType)
            }

        }
    }
}

fun Instant.roundToSeconds(): Instant {
    return Instant.fromEpochSeconds(this.epochSeconds)
}

@Composable
fun EntityTypeMetrics(aggregator: AggregatorService, entityType: EntityTypeConfiguration) {
    var metricsPeriod: TimeRange by remember {
        mutableStateOf(TimeRange(PeriodType.Days.periodOf(1)))
    }
    var loadedMetrics by remember { mutableStateOf<List<MetricData>?>(null) }

    // Remember the chart details state
    var chartDetails by remember { mutableStateOf<ChartDetails?>(null) }

    LaunchedEffect(entityType.type, metricsPeriod) {
        val period = metricsPeriod
        loadedMetrics = null
        val now = Clock.System.now().roundToSeconds()
        val range = period.range(now)
        try {
            val result = aggregator.clientApi.getMetrics(
                GetMetricDataRequest(
                    range.start, range.endInclusive, listOf("update", "add", "remove").map { updateType ->
                        MetricDataRequest(
                            "${updateType}Events",
                            null,
                            MetricStat(
                                "AtlasEntityPublished",
                                "aggregator/${aggregator.context.configuration.stage}",
                                listOf(
                                    Dimension("EntityType", entityType.type),
                                    Dimension("UpdateType", "com.siriusxm.unifiedcontentbus.${updateType}")
                                ),
                                period.groupingAmount(25).inWholeSeconds.toInt(),
                                "Sum"
                            )
                        )
                    }
                )
            ).data

            loadedMetrics = result

            // Process the data and create chart directly
            val includeDay = period.duration().inWholeHours >= 23
            val groupingAmount = period.groupingAmount(25)
            val includeMinutes = groupingAmount < 1.hours

            val groupedData = result.associateWith { metricData ->
                metricData.allData(period, now, groupingAmount)
            }

            chartDetails = ChartDetails {
                type = ChartType.BAR

                data {
                    // Create datasets directly from the grouped data
                    groupedData.forEach { (metricData, dataPoints) ->
                        dataset {
                            label = metricData.id.replace("Events", "")

                            // Use the points builder for time-series data
                            points {
                                dataPoints.forEach { point ->
                                    // Render chart axis labels in the globally-selected timezone
                                    // (header picker), not the browser's system zone.
                                    val timeLabel = if (includeDay) {
                                        point.ts.toLocalDateTimeIn(currentTimeZoneId()).let {
                                            "${it.date} ${it.toAMPMTimeString(includeMinutes = includeMinutes)}"
                                        }
                                    } else {
                                        point.ts.toLocalDateTimeIn(currentTimeZoneId()).toAMPMTimeString(false, false)
                                    }
                                    point(timeLabel, point.value.toInt())
                                }
                            }

                            // Apply colors from the original colors map
                            val colorArray = colors[metricData.id]
                            if (colorArray != null && colorArray.isNotEmpty()) {
                                backgroundColor = SingleColor(colorArray[0])
                            }
                        }
                    }
                }

                options {
                    height = 300.px
                    scales {
                        x {
                            stacked = true
                        }
                        y {
                            stacked = true
                            beginAtZero = true
                            title {
                                display = true
                                text = "Events Count"
                            }
                        }
                    }

                    plugins {
                        legend {
                            display = true
                        }
                        title {
                            display = true
                            text = "Published Entities"
                        }
                    }
                }
            }
        } catch (t: Throwable) {
            t.printStackTrace()
        }
    }

    Div({
        classes(EntityTypesStyles.metricsHeader)
    }) {
        timeRangeSelect(selected = metricsPeriod) {
            metricsPeriod = it ?: TimeRange(PeriodType.Days.periodOf(1))
        }
    }

    waitUntilNonNull(chartDetails) { details ->
        box({
            title = "Published Entities"

            header({
                actionContent {
                    if (loadedMetrics == null) {
                        // we're in the process of loading a new dataset
                        spinner(size = Size.SMALL)
                    }
                }
            })
        }) {
            Chart(details)
        }
    }

    EntityCountHistory(aggregator, entityType)
}

/**
 * One day of recorded count history for a single entity type.
 *
 * [count] is null when that day's snapshot did not count this type at all, which is *not* the
 * same as counting zero of them. See [countSeriesFor].
 */
internal data class EntityCountPoint(val date: LocalDate, val count: Long?)

/**
 * Terminal states of a count-history load, kept explicit so an empty result cannot be confused
 * with a failure or with "still loading" — the history only starts when the nightly snapshot job
 * first ran and cannot be backfilled, so an empty range is a routine outcome.
 */
private sealed interface CountHistoryState {
    data object Loading : CountHistoryState
    data object Empty : CountHistoryState
    data class Failed(val message: String) : CountHistoryState
    data class Loaded(val chart: ChartDetails) : CountHistoryState
}

/**
 * Project raw history entries onto a single type's daily series.
 *
 * Reads `counts[type]` and *keeps the null*. Never `?: 0`: an absent key means that day's
 * snapshot did not count this type, and substituting zero would draw a cliff to the axis
 * implying no entities of the type exist, when the truth is only that nothing was measured. A
 * genuine count of zero arrives as `0` and is preserved as zero, so the two stay distinct.
 *
 * Each entry is dated by its `dataTimeStamp` **in UTC**, deliberately ignoring the header
 * timezone picker. The timestamp is not a moment an event happened — it is the identifier of the
 * calendar day a daily snapshot describes, written at UTC midnight and used verbatim as that
 * day's row key server-side. Shifting it into a viewer's zone would rename the day rather than
 * re-express it: in any negative-offset zone the whole axis slides back one, so the newest
 * snapshot would be labelled yesterday and disagree with the count on the Definition tab. Charts
 * of *event* times in this view do honour the picker, because for those the instant is the datum.
 *
 * Entries arrive oldest-first from the API and are left in that order. Days with no entry at all
 * are not synthesised — the series covers exactly the days the API returned.
 */
internal fun List<EntityCountHistoryEntry>.countSeriesFor(
    type: String
): List<EntityCountPoint> = map {
    EntityCountPoint(it.dataTimeStamp.toLocalDateIn("UTC"), it.counts[type])
}

/**
 * Page size for a history request. One entry per day means 500 covers roughly sixteen months, so
 * realistic ranges resolve in a single call; the paging loop exists for the longer ones.
 */
private const val COUNT_HISTORY_PAGE_SIZE = 500

/**
 * Trend chart of the approximate daily entity count for a single type.
 *
 * Reads the nightly snapshot history from the aggregator, one point per UTC calendar day, over a
 * day-granularity range that defaults to the trailing four weeks. The chart is a line rather than
 * bars because the value is a level trending over time, not event volume accumulated per bucket.
 *
 * Days the snapshot did not count this type render as gaps in the line, never as zero — the
 * distinction matters because a drop to zero would read as "this type has no entities" when it
 * actually means "nothing was measured".
 */
@Composable
fun EntityCountHistory(aggregator: AggregatorService, entityType: EntityTypeConfiguration) {
    // Four weeks rather than a literal 30 days so the default matches a top-line option in the
    // day selector, and so renders as a selected preset rather than a custom range.
    val defaultRange = remember { PeriodType.Weeks.periodOf(4).dayRange() }
    var countRange: TimeRange by remember { mutableStateOf(defaultRange) }
    var state: CountHistoryState by remember { mutableStateOf(CountHistoryState.Loading) }

    // Deliberately not keyed on currentTimeZoneId(): the day labels are UTC snapshot days, so the
    // header timezone picker does not affect this series. See [countSeriesFor].
    LaunchedEffect(entityType.type, countRange) {
        state = CountHistoryState.Loading
        val range = countRange.range(Clock.System.now().roundToSeconds())

        // The API rejects an inverted range with a 400. The day pickers constrain each other so
        // this should be unreachable, but failing closed here beats surfacing a server error.
        if (range.start > range.endInclusive) {
            state = CountHistoryState.Failed("Select a start date on or before the end date.")
            return@LaunchedEffect
        }

        try {
            val entries = mutableListOf<EntityCountHistoryEntry>()
            var nextToken: String? = null
            do {
                val page = aggregator.clientApi.getEntityCountHistory(
                    types = entityType.type,
                    start = range.start,
                    end = range.endInclusive,
                    limit = COUNT_HISTORY_PAGE_SIZE,
                    nextToken = nextToken
                )
                // Pages advance forward in time, so each one appends to the end of the series.
                entries += page.entries
                nextToken = page.nextToken
            } while (nextToken != null)

            val series = entries.countSeriesFor(entityType.type)
            state = if (series.isEmpty()) {
                CountHistoryState.Empty
            } else {
                CountHistoryState.Loaded(series.toCountHistoryChart(entityType))
            }
        } catch (t: Throwable) {
            t.printStackTrace()
            state = CountHistoryState.Failed(
                t.message?.takeIf { it.isNotBlank() } ?: "Unable to load entity count history."
            )
        }
    }

    Div({
        classes(EntityTypesStyles.metricsHeader)
    }) {
        dateRangeSelect(selected = countRange) {
            countRange = it ?: defaultRange
        }
    }

    box({
        title = "Entity Count"

        header({
            actionContent {
                if (state is CountHistoryState.Loading) {
                    spinner(size = Size.SMALL)
                }
            }
        })
    }) {
        when (val current = state) {
            is CountHistoryState.Loading -> Unit // the header carries the spinner
            is CountHistoryState.Empty -> boxMessage("No count history for this range.")
            is CountHistoryState.Failed -> boxMessage(current.message)
            is CountHistoryState.Loaded -> Chart(current.chart)
        }
    }
}

/**
 * Build the count-history line chart for [entityType] from an already-projected daily series.
 *
 * Counts are handed to Chart.js as [Double]s, not as the raw [Long]s from the API: Kotlin/JS
 * compiles Long to a class object rather than a JS number, and passing one through would leave
 * Chart.js with an unusable value and an empty canvas. Counts reach ~14 million, well inside the
 * range a Double holds exactly.
 *
 * A null count becomes a null data point, which Chart.js renders as a break in the line. The
 * y-axis is deliberately left to auto-scale: these series trend within a narrow band (a few
 * thousand entities moving by tens), and anchoring the axis at zero would flatten real movement
 * into a straight line.
 */
private fun List<EntityCountPoint>.toCountHistoryChart(
    entityType: EntityTypeConfiguration
): ChartDetails = ChartDetails {
    type = ChartType.LINE

    data {
        dataset {
            label = entityType.name
            spanGaps = false // an uncounted day is a gap in the line, never a zero
            borderColor = SingleColor(CHART_SERIES_BLUE)
            tension = 0.1
            pointRadius = 3

            points {
                this@toCountHistoryChart.forEach { entry ->
                    point(entry.date.toString(), entry.count?.toDouble())
                }
            }
        }
    }

    options {
        height = 300.px
        scales {
            x {
                title {
                    display = true
                    // Labelled UTC because a snapshot day is a UTC calendar day, not a local one.
                    text = "Date (UTC)"
                }
            }
            y {
                title {
                    display = true
                    text = "Entities"
                }
            }
        }

        plugins {
            legend {
                display = false // a single series needs no key
            }
        }
    }
}

/**
 * Display the documentation for an entity Smithy model
 */
@Composable
fun EntityModel(
    context: AggregatorService,
    type: EntityTypeConfiguration,
    controller: ExpandingTableController? = null
) {
    Style(EntityModelStyles)

    val shapeId = type.shapeId
    val model = context.entityModel
    if (shapeId != null && model != null) {
        val shape = model.getShape(shapeId)
        if (shape != null) {
            Shape(shape, controller)
        }
    }
}

/**
 * Collapsible JSON panel that seeds the validator with the generated example payload.
 * The preview is shown by default so users can compare the schema definition and run
 * validation actions without leaving the definition view.
 */
@Composable
fun ExampleJsonPanel(
    aggregator: AggregatorService,
    entityType: EntityTypeConfiguration,
    exampleJson: String,
    expanded: Boolean,
    onExpandedChange: (Boolean) -> Unit
) {
    val coroutineScope = rememberCoroutineScope()
    var showComparisonDialog by remember { mutableStateOf(false) }
    var comparisonResult by remember { mutableStateOf<EntityComparisonResult?>(null) }
    var loadingComparison by remember { mutableStateOf(false) }
    var comparisonError by remember { mutableStateOf<String?>(null) }

    val description = if (exampleJson.isBlank()) {
        "Paste JSON to validate it against the schema."
    } else {
        "Explore the JSON schema and validate your data."
    }
    EntityTypeValidationPanel(
        aggregator = aggregator,
        entityType = entityType,
        initialJson = exampleJson,
        message = "",
        defaultMode = if (exampleJson.isNotBlank()) ValidationPanelMode.Preview else ValidationPanelMode.Edit,
        initialPreviewJson = exampleJson.takeIf { it.isNotBlank() },
        resetExampleJson = exampleJson.takeIf { it.isNotBlank() },
        panelContainer = { inner ->
            box({
                title = "JSON"
                enableExpanding(expanded, onExpandedChange)
                header(builder = {
                    instruction = description
                })
            }) {
                inner()
            }
        },
        extraActions = { currentJson ->
            button {
                title = "Compare with Valid"
                icon = "compare_arrows"
                enabled = currentJson.isNotBlank() && !loadingComparison
                showProgressOnAction = true
                action {
                    loadingComparison = true
                    comparisonError = null
                    try {
                        comparisonResult = compareJsonWithReference(
                            aggregator = aggregator,
                            jsonInput = currentJson,
                            entityTypeName = entityType.type
                        )
                        showComparisonDialog = true
                    } catch (e: Exception) {
                        comparisonError = e.message ?: "Failed to fetch valid entity for comparison"
                        console.error("Error comparing entities:", e)
                    } finally {
                        loadingComparison = false
                    }
                }
            }

            comparisonError?.let { error ->
                Span({
                    style {
                        color(SXMUI.stopColor.value())
                        fontSize(0.9.em)
                    }
                }) {
                    Text(error)
                }
            }
        }
    )

    if (showComparisonDialog && comparisonResult != null) {
        EntityComparisonDialog(
            comparisonResult = comparisonResult!!,
            onClose = { showComparisonDialog = false }
        )
    }
}

private const val SAMPLE_UUID = "827f657d-b290-449c-a18a-5f59ce83c5cf"

private fun patchExampleJson(typeName: String?, element: JsonElement, idPattern: String? = null): JsonElement {
    val stripped = element.removeTypedEntityId()
    val patched = patchWithUuid(stripped, typeName)
    return patchIdForPattern(patched, idPattern)
}

/**
 * Replaces the example `id` with a value that satisfies the entity type's [idPattern].
 *
 * Earlier this only prepended the literal prefix to whatever `id` already held, which produced
 * ids like `RE:827f657d-…` — valid only when the pattern body happened to be UUID-shaped. Instead
 * we synthesise the whole id from the pattern via [exampleFromPattern] so the generated example
 * always matches its own configured pattern (and therefore passes [checkIdPattern] validation).
 * When the pattern can't be synthesised we fall back to prepending the extracted prefix, and if
 * even that isn't available we leave the id untouched.
 */
private fun patchIdForPattern(element: JsonElement, idPattern: String?): JsonElement {
    if (idPattern.isNullOrBlank()) return element
    val obj = element as? JsonObject ?: return element
    val currentId = (obj["id"] as? JsonPrimitive)?.contentOrNull ?: return element

    val generated = exampleFromPattern(idPattern)
    val newId = when {
        // A synthesised value that matches the pattern is always preferable.
        generated != null && runCatching { Regex(idPattern).matches(generated) }.getOrDefault(false) ->
            generated
        // Otherwise fall back to prepending the literal prefix if the id lacks it.
        else -> {
            val prefix = idPrefixFromPattern(idPattern) ?: return element
            if (currentId.startsWith(prefix)) return element
            "$prefix$currentId"
        }
    }

    val patched = obj.toMutableMap()
    patched["id"] = JsonPrimitive(newId)
    return JsonObject(patched)
}

private fun JsonElement.removeTypedEntityId(): JsonElement {
    return when (this) {
        is JsonObject -> {
            val filtered = filterKeys { it != "typedEntityId" }
                .mapValues { (_, v) -> v.removeTypedEntityId() }
            JsonObject(filtered)
        }
        is JsonArray -> JsonArray(map { it.removeTypedEntityId() })
        else -> this
    }
}

private fun JsonElement.overrideTypeField(typeValue: String): JsonElement {
    val obj = this as? JsonObject ?: return this
    val patched = obj.toMutableMap()
    patched["type"] = JsonPrimitive(typeValue)
    return JsonObject(patched)
}

private fun JsonElement.overrideVersionField(version: String): JsonElement {
    val obj = this as? JsonObject ?: return this
    if (!obj.containsKey("version")) return this
    val patched = obj.toMutableMap()
    patched["version"] = JsonPrimitive(version)
    return JsonObject(patched)
}


private fun patchRootAndNestedIds(element: JsonElement, targets: List<String>): JsonElement {
    val obj = element as? JsonObject ?: return element
    var current = obj.toMutableMap()
    if ("id" in targets) {
        current["id"] = JsonPrimitive(SAMPLE_UUID)
    }
    if ("artistId" in targets) {
        current["artistId"] = JsonPrimitive(SAMPLE_UUID)
    }
    if (targets.any { it.startsWith("entities[0].id") }) {
        val entities = obj["entities"]
        if (entities is JsonArray && entities.isNotEmpty()) {
            val first = entities.firstOrNull() as? JsonObject
            if (first != null) {
                val updated = first.toMutableMap()
                updated["id"] = JsonPrimitive(SAMPLE_UUID)
                val mutableEntities = entities.toMutableList()
                mutableEntities[0] = JsonObject(updated)
                current["entities"] = JsonArray(mutableEntities)
            }
        }
    }
    return JsonObject(current)
}

private const val SAMPLE_ENCODED_ID = "A0B1C2D3E4"

private fun patchWithUuid(element: JsonElement, typeName: String? = null): JsonElement {
    return when (element) {
        is JsonObject -> {
            val patched = element.toMutableMap()
            element.forEach { (key, value) ->
                val lower = key.lowercase()
                patched[key] = when {
                    value is JsonPrimitive && value.isString && lower == "encodedid" ->
                        JsonPrimitive(SAMPLE_ENCODED_ID)

                    value is JsonPrimitive && value.isString && value.content == "string" &&
                        (lower.endsWith("id") && lower != "originid" || lower == "episode" || lower == "track" || lower == "show" || lower == "channel" || lower.endsWith("team") || lower == "league") ->
                        JsonPrimitive(SAMPLE_UUID)

                    value is JsonArray && lower == "value" ->
                        JsonArray(listOf(JsonPrimitive(SAMPLE_UUID)))
                    lower == "parentchannels" && value is JsonArray ->
                        JsonArray(listOf(JsonPrimitive(SAMPLE_UUID)))

                    lower == "options" && value is JsonArray && value.size == 1
                        && typeName?.contains("poll", ignoreCase = true) == true ->
                        JsonArray(listOf(patchWithUuid(value[0], typeName), patchWithUuid(value[0], typeName)))

                    (lower == "parents" || lower == "categories" || lower == "parentchannels" || lower == "channels" || lower == "satonlychannels" || lower == "satavailablechannels") && value is JsonArray -> patchArrayOfScalars(value)
                    lower == "relatedentities" && value is JsonArray -> patchRelatedEntities(value)
                    else -> patchWithUuid(value, typeName)
                }
            }
            JsonObject(patched)
        }
        is JsonArray -> JsonArray(element.map { patchWithUuid(it, typeName) })
        else -> element
    }
}

private fun patchValueField(element: JsonElement): JsonElement {
    val obj = element as? JsonObject ?: return element
    val patched = obj.toMutableMap()
    patched["value"] = JsonArray(listOf(JsonPrimitive(SAMPLE_UUID)))
    return JsonObject(patched)
}


private fun patchArrayOfScalars(array: JsonArray): JsonArray {
    return JsonArray(array.map { elem ->
        if (elem is JsonPrimitive && elem.isString) {
            JsonPrimitive(SAMPLE_UUID)
        } else {
            patchWithUuid(elem)
        }
    })
}

private fun patchRelatedEntities(array: JsonArray): JsonArray {
    return JsonArray(array.map { entry ->
        val obj = entry as? JsonObject ?: return@map patchWithUuid(entry)
        val patched = obj.toMutableMap()
        patched["value"] = JsonArray(listOf(JsonPrimitive(SAMPLE_UUID)))
        JsonObject(patched)
    })
}
